import { useState, useCallback, useMemo } from 'react'
import { minBy, cloneDeep } from 'lodash'
import { InsightData } from 'schemas/action/InsightDataSchema'
import TimeInterval from 'enums/TimeInterval'
import { toFilter } from 'utils/widgetUtil'
import { subTz } from 'utils/timeZoneUtil'
import { usePageConfigUser } from 'hooks/usePageConfig'
import { TimelineWidget } from '../../../../types'
import useXCoordinates from './useXCoordinates'

const useSelectedInsights = (widgetSetting: TimelineWidget) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedInsights, setSelectedInsights] = useState<InsightData[]>([])
  const filter = toFilter(widgetSetting)
  const endTime = filter.publishedDate.end
  const { localizedXCoordinates, originalXCoordinates } = useXCoordinates(widgetSetting)
  const pageConfigUser = usePageConfigUser()

  const widgetSettingWithSelectedInsights = useMemo(
    () => ({
      ...cloneDeep(widgetSetting),
      startDatetime: selectedInsights?.[0]?.startDatetime,
      endDatetime: selectedInsights?.[0]?.endDatetime,
      timeInterval: selectedInsights?.[0]?.timeInterval
    }),
    [widgetSetting, selectedInsights]
  )

  const handleClick = useCallback(
    (timestamp: number) => {
      const currentIndex = localizedXCoordinates.findIndex((x) => x === timestamp)
      const nextIndex = currentIndex < localizedXCoordinates.length - 1 ? currentIndex + 1 : -1
      const originalStartTimestamp = originalXCoordinates[currentIndex]!
      const originalEndTimestamp = originalXCoordinates[nextIndex]

      setSelectedInsights([
        {
          startDatetime: originalStartTimestamp,
          endDatetime: originalEndTimestamp || endTime,
          timeInterval: TimeInterval.CUSTOM
        }
      ])
      setIsModalOpen(true)
    },
    [setIsModalOpen, originalXCoordinates, endTime, localizedXCoordinates]
  )

  const onClick = useCallback(
    (event) => {
      handleClick(event.point.x)
    },
    [handleClick]
  )

  const onClickChart = useCallback(
    (event) => {
      const position = event.xAxis[0].value
      if (Number.isNaN(position)) {
        return
      }

      const nearestTimestamp = minBy(localizedXCoordinates, (timestamp: number) =>
        Math.abs(timestamp! - position)
      )
      handleClick(nearestTimestamp!)
    },
    [localizedXCoordinates, handleClick]
  )

  const onSelection = useCallback(
    (event) => {
      setSelectedInsights([
        {
          startDatetime: subTz(event.xAxis[0].min, pageConfigUser.timeZone),
          endDatetime: subTz(event.xAxis[0].max, pageConfigUser.timeZone),
          timeInterval: TimeInterval.CUSTOM
        }
      ])
      setIsModalOpen(true)
    },
    [pageConfigUser.timeZone]
  )

  const onClose = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  return {
    isModalOpen,
    onClick,
    onClickChart,
    onSelection,
    onClose,
    selectedInsights,
    widgetSettingWithSelectedInsights
  }
}

export default useSelectedInsights
